import type { ComputedRef, WritableComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useDayjs } from '@shared/lib/format'
import { useFiltersDraft } from '@shared/lib/filters'

import { useSubscriptionsStore } from './store'
import { useSubscriptionOptions } from './subscription-options'

interface SubscriptionsFilters {
  search: string | null
  statuses: string[]
  from: string | null
  to: string | null
}

interface FiltersSubscriptionsReturn {
  refSearch: WritableComputedRef<string>
  refDateRange: WritableComputedRef<Date[] | null>
  refStatuses: WritableComputedRef<string[]>
  statusOptions: ComputedRef<AppSelectOption[]>
  isLoading: ComputedRef<boolean>
  clearFilters: () => void
  submitSearch: () => Promise<void>
}

/** The controls only prepare the filters: Search (or Enter in the search field) applies them. */
export const useFiltersSubscriptions = (): FiltersSubscriptionsReturn => {
  const store = useSubscriptionsStore()
  const dayjs = useDayjs()
  const { statusOptions } = useSubscriptionOptions()

  const { refDraft, isLoading, patchDraft, search, clearFilters } = useFiltersDraft<SubscriptionsFilters>({
    applied: () => ({
      search: store.getFilterSearch,
      statuses: store.getFilterStatuses,
      from: store.getFilterFrom,
      to: store.getFilterTo,
    }),
    onApply: (filters) => {
      store.setFilterSearch(filters.search?.trim() || null)
      store.setFilterStatuses(filters.statuses)
      store.setFilterFrom(filters.from)
      store.setFilterTo(filters.to)
    },
    onReset: () => store.resetFilters(),
    toQuery: filters => ({
      q: filters.search || undefined,
      status: filters.statuses.length ? filters.statuses : undefined,
      from: filters.from || undefined,
      to: filters.to || undefined,
    }),
    loading: () => store.isLoading,
  })

  const refSearch = computed<string>({
    get: () => refDraft.value.search ?? '',
    set: (value) => {
      patchDraft({ search: value || null })
    },
  })

  const refDateRange = computed<Date[] | null>({
    get: () => {
      const { from, to } = refDraft.value
      if (!from || !to) return null

      const fromDate = dayjs(from)
      const toDate = dayjs(to)

      return fromDate.isValid() && toDate.isValid() ? [fromDate.toDate(), toDate.toDate()] : null
    },
    set: (value) => {
      const [from, to] = value ?? []

      patchDraft(from && to
        ? { from: dayjs(from).format(DateFormat.Default), to: dayjs(to).format(DateFormat.Default) }
        : { from: null, to: null })
    },
  })

  const refStatuses = computed<string[]>({
    get: () => refDraft.value.statuses,
    set: (value) => {
      patchDraft({ statuses: value })
    },
  })

  return {
    refSearch,
    refDateRange,
    refStatuses,
    statusOptions,
    isLoading,
    clearFilters,
    submitSearch: search,
  }
}
